import React from 'react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@pagespeedify/ui';
import { Trash2 as Trash2Icon, CheckCircleIcon, AlertCircleIcon, ClockIcon, MinusIcon, PlayIcon, EyeIcon, EyeOffIcon } from 'lucide-react';
import { useDeleteQueue, QueuedDeleteTask } from './DeleteQueueContext';
import { useApi } from '../../lib/apiClient';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

const DeleteQueuePanel: React.FC = () => {
  const {
    queuedDeleteTasks,
    removeTaskFromDeleteQueue,
    clearDeleteQueue,
    clearCompletedDeletes,
    updateDeleteTaskStatus,
    isDeleteQueueVisible,
    setDeleteQueueVisible,
    isProcessingDelete,
    setIsProcessingDelete
  } = useDeleteQueue();
  const apiClient = useApi();
  const queryClient = useQueryClient();

  if (!isDeleteQueueVisible && queuedDeleteTasks.length === 0) {
    return null;
  }

  const getStatusIcon = (status: QueuedDeleteTask['status']) => {
    switch (status) {
      case 'queued':
        return <ClockIcon className="h-4 w-4 text-blue-500" />;
      case 'deleting':
        return <div className="h-4 w-4 animate-spin rounded-full border-2 border-blue-500 border-t-transparent" />;
      case 'deleted':
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircleIcon className="h-4 w-4 text-red-500" />;
      default:
        return <ClockIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusText = (status: QueuedDeleteTask['status']) => {
    switch (status) {
      case 'queued': return 'Queued';
      case 'deleting': return 'Deleting...';
      case 'deleted': return 'Deleted';
      case 'failed': return 'Failed';
      default: return 'Unknown';
    }
  };

  const handleProcessDeleteQueue = async () => {
    if (isProcessingDelete) return;
    const queuedItems = queuedDeleteTasks.filter(task => task.status === 'queued');
    if (queuedItems.length === 0) {
      toast.info('No tasks in delete queue to process');
      return;
    }
    setIsProcessingDelete(true);
    // Mark all as deleting for UI
    queuedItems.forEach(task => updateDeleteTaskStatus(task.id, 'deleting'));
    try {
      // Batch delete API call
      const response = await apiClient.post('/tasks/delete',
        queuedItems.map(task => task.id)
      );
      // Mark all as deleted if successful
      queuedItems.forEach(task => updateDeleteTaskStatus(task.id, 'deleted'));
      toast.success(`${queuedItems.length} task${queuedItems.length !== 1 ? 's' : ''} deleted`);
      // Invalidate both task queries to ensure both pages update
      await queryClient.invalidateQueries({ queryKey: ['tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['ai-tasks-list'] });
      await queryClient.invalidateQueries({ queryKey: ['image-optimization-tasks'] });
      await queryClient.invalidateQueries({ queryKey: ['monitoredUrls'] });

      // Force a refetch of the ai-tasks-list query
      await queryClient.refetchQueries({ queryKey: ['ai-tasks-list'], exact: true });
      // Do not auto-clear completed deletes, so they remain filtered in the UI until dismissed
      // clearCompletedDeletes();
    } catch (err) {
      // If batch fails, mark all as failed
      queuedItems.forEach(task => updateDeleteTaskStatus(task.id, 'failed', err?.message || 'Batch failed'));
      toast.error('Unable to delete tasks. Please try again.');
    }
    setIsProcessingDelete(false);
  };

  const queuedCount = queuedDeleteTasks.filter(t => t.status === 'queued').length;
  const deletedCount = queuedDeleteTasks.filter(t => t.status === 'deleted').length;
  const failedCount = queuedDeleteTasks.filter(t => t.status === 'failed').length;

  return (
    <div className="fixed right-4 bottom-4 z-50 w-96 max-h-[70vh] overflow-hidden">
      <Card className="shadow-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-semibold flex items-center gap-2">
              <Trash2Icon className="h-5 w-5" />
              Delete Queue ({queuedDeleteTasks.length})
            </CardTitle>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setDeleteQueueVisible(!isDeleteQueueVisible)}
                className="h-8 w-8 p-0"
              >
                {isDeleteQueueVisible ? <EyeOffIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDeleteQueue}
                className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                disabled={isProcessingDelete}
              >
                <Trash2Icon className="h-4 w-4" />
              </Button>
            </div>
          </div>
          {queuedDeleteTasks.length > 0 && (
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {queuedCount > 0 && <span className="text-blue-600">{queuedCount} queued</span>}
              {deletedCount > 0 && <span className="text-green-600 ml-2">{deletedCount} deleted</span>}
              {failedCount > 0 && <span className="text-red-600 ml-2">{failedCount} failed</span>}
            </div>
          )}
        </CardHeader>
        {isDeleteQueueVisible && (
          <CardContent className="pt-0">
            {queuedDeleteTasks.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">
                No tasks in delete queue. Select tasks to delete.
              </p>
            ) : (
              <>
                <div className="max-h-60 overflow-y-auto space-y-2 mb-4">
                  {queuedDeleteTasks.map((task) => (
                    <div
                      key={task.id}
                      className="flex items-start gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded text-sm"
                    >
                      <div className="flex-shrink-0 mt-0.5">
                        {getStatusIcon(task.status)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">{task.title}</p>
                        <div className="flex items-center gap-2 text-xs text-gray-500">
                          {task.type && (
                            <span className="px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-300 uppercase text-[10px]">
                              {task.type}
                            </span>
                          )}
                          <span>{getStatusText(task.status)}</span>
                          {task.error && <span className="text-red-500 ml-2">{task.error}</span>}
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeTaskFromDeleteQueue(task.id)}
                        className="h-6 w-6 p-0 text-gray-400 hover:text-red-500"
                        disabled={isProcessingDelete && task.status === 'deleting'}
                      >
                        <MinusIcon className="h-3 w-3" />
                      </Button>
                    </div>
                  ))}
                </div>
                {queuedCount > 0 && (
                  <div className="flex gap-2">
                    <Button
                      onClick={handleProcessDeleteQueue}
                      disabled={isProcessingDelete}
                      className="flex-1"
                      size="sm"
                    >
                      {isProcessingDelete ? (
                        <>
                          <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                          Deleting Tasks...
                        </>
                      ) : (
                        <>
                          <PlayIcon className="h-4 w-4 mr-2" />
                          Delete {queuedCount} Task{queuedCount !== 1 ? 's' : ''}
                        </>
                      )}
                    </Button>
                    {(deletedCount > 0 || failedCount > 0) && (
                      <Button
                        onClick={clearCompletedDeletes}
                        disabled={isProcessingDelete}
                        variant="outline"
                        size="sm"
                        className="px-2"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                )}
              </>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default DeleteQueuePanel;
